<template>
  <AccessibleDialog
    :open="showModal"
    title-id="public-offer-title"
    :close-on-overlay="false"
    :close-on-escape="false"
    content-class="public-offer-dialog"
  >
    <div class="modal-content">
      <div class="modal-header">
        <div>
          <p class="modal-eyebrow">{{ t('walletTransferBan.documentType') }}</p>
          <h2 id="public-offer-title">{{ t('walletTransferBan.offerTitle') }}</h2>
        </div>
        <span class="offer-version">№012 · 21.04.2026</span>
      </div>
      <div
        ref="offerScroll"
        class="modal-body"
        tabindex="0"
        :aria-label="t('walletTransferBan.scrollAreaLabel')"
        @scroll="checkScrollPosition"
      >
        <UserAgreementView embedded />
        <div class="scroll-end" aria-hidden="true"></div>
      </div>
      <div v-if="!hasReadToEnd" class="scroll-prompt" aria-live="polite">
        <span>{{ t('walletTransferBan.scrollPrompt') }}</span>
        <span class="scroll-arrow" aria-hidden="true">↓</span>
      </div>
      <div v-else class="modal-footer">
        <label class="checkbox-label">
          <input type="checkbox" v-model="agreed" />
          <span>{{ t('walletTransferBan.offerConsent') }}</span>
        </label>
        <p v-if="submitError" class="submit-error" role="alert">{{ submitError }}</p>
        <button
          type="button"
          class="btn-primary"
          :disabled="!agreed || isSubmitting"
          @click="accept"
        >
          {{ isSubmitting ? t('common.loading') : t('walletTransferBan.acceptOffer') }}
        </button>
      </div>
    </div>
  </AccessibleDialog>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue'
import { useI18n } from '@/composables/useI18n'
import apiClient from '@/api/apiClient'
import AccessibleDialog from '@/components/AccessibleDialog.vue'
import UserAgreementView from '@/views/legal/UserAgreementView.vue'

const { t } = useI18n()
const CONSENT_TYPE = 'public_offer_012'
const CONSENT_STORAGE_KEY = 'consent_public_offer_012'
const showModal = ref(false)
const agreed = ref(false)
const hasReadToEnd = ref(false)
const isSubmitting = ref(false)
const submitError = ref('')
const offerScroll = ref<HTMLElement | null>(null)

function markConsentLocally() {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, '1')
  } catch {
    /* ignore quota / private mode */
  }
}

function hasLocalConsent() {
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function checkScrollPosition() {
  const element = offerScroll.value
  if (!element || hasReadToEnd.value) return

  const threshold = 8
  hasReadToEnd.value =
    element.scrollTop + element.clientHeight >= element.scrollHeight - threshold
}

async function accept() {
  if (!agreed.value || !hasReadToEnd.value || isSubmitting.value) return
  isSubmitting.value = true
  submitError.value = ''

  try {
    await apiClient.post('/users/me/consents', {
      consent_type: CONSENT_TYPE,
      agreed: true,
    })
    markConsentLocally()
    showModal.value = false
  } catch {
    submitError.value = t('walletTransferBan.submitError')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(async () => {
  try {
    const response = await apiClient.get('/users/me/consents')
    const consent = response.data?.consents?.[CONSENT_TYPE]
    if (consent?.agreed === true) {
      markConsentLocally()
      return
    }
  } catch {
    // При сбое API не показываем оферту снова, если согласие уже сохраняли локально.
    if (hasLocalConsent()) return
    return
  }

  showModal.value = true
  await nextTick()
  checkScrollPosition()
  offerScroll.value?.focus({ preventScroll: true })
})
</script>

<style scoped>
.modal-content {
  background: var(--background-secondary, #fff);
  border-radius: 16px;
  max-width: 760px;
  width: 100%;
  height: min(820px, calc(100vh - 2rem));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-primary, #eee);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
}

.modal-eyebrow {
  margin: 0 0 0.25rem;
  color: var(--primary-green, #42c878);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.offer-version {
  flex-shrink: 0;
  padding: 0.4rem 0.65rem;
  border: 1px solid var(--border-primary, #eee);
  border-radius: 999px;
  color: var(--text-secondary, #666);
  font-size: 0.75rem;
}

.modal-body {
  padding: 1.5rem 1.75rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  scroll-behavior: smooth;
}

.modal-body:focus-visible {
  outline: 3px solid var(--primary-green, #42c878);
  outline-offset: -3px;
}

.scroll-end {
  height: 1px;
}

.scroll-prompt {
  min-height: 56px;
  padding: 0.85rem 1.5rem;
  border-top: 1px solid var(--border-primary, #eee);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-secondary, #666);
  font-size: 0.875rem;
  font-weight: 600;
  background: var(--background-secondary, #fff);
}

.scroll-arrow {
  color: var(--primary-green, #42c878);
  font-size: 1.25rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-primary, #eee);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-shrink: 0;
}

.submit-error {
  margin: 0;
  color: var(--error-color, #dc2626);
  font-size: 0.875rem;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--text-secondary, #666);
}

.checkbox-label input[type="checkbox"] {
  margin-top: 0.2rem;
  flex-shrink: 0;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  background: var(--primary-blue, #2563eb);
  color: #0b0e11;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  min-height: 44px;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .modal-content {
    height: calc(100vh - 1rem);
    border-radius: 12px;
  }

  .modal-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .modal-header h2 {
    font-size: 1rem;
  }

  .offer-version {
    padding: 0.3rem 0.5rem;
    font-size: 0.65rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .modal-footer {
    padding: 0.875rem 1rem;
  }
}
</style>
